import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  Directive,
  EffectCleanupRegisterFn,
  ElementRef,
  Renderer2,
  effect,
  inject,
  input,
} from '@angular/core';
import { EduContentBookQueries, EduContentTocLearningDomainFeature, EduContentTocQueries } from '@campus/dal';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { actions } from '../+state';
import { StyleInjectorService } from '../services';
import {
  ScopeApplication,
  applicationsEqual,
  buildAncestorChain,
  buildExplicitApplications,
  buildScopeKey,
  buildTocAndLdApplications,
  pickDeepestTocId,
} from '../utils';

/**
 * Controls how `ScopedPaletteDirective` resolves and loads palettes.
 *
 * - `'auto'` (default): dispatch `ensureLoadedFor` for the most specific id and
 *   walk the full toc ancestor chain (toc → all parent tocs → book → method)
 *   via the DAL store. Each toc in the chain contributes a depth-aware class,
 *   and each linked LD wins at the deepest toc it is linked to. The right
 *   choice for ad-hoc consumers (dialogs, overlays) that may render before any
 *   palette has been fetched.
 * - `'skip-fetch'`: do **not** dispatch `ensureLoadedFor`, but still walk the
 *   full chain. Use this when the route resolver has already loaded palettes
 *   for this scope (including ancestors when applicable).
 * - `'skip-ancestors'`: dispatch `ensureLoadedFor`, but apply classes only for
 *   the ids passed in explicitly — no DAL store lookups, no toc ancestor
 *   walk. Each scope is treated as depth=0 (legacy single-toc cascade).
 * - `'manual'`: skip both. The directive applies classes for the explicitly
 *   passed ids and dispatches nothing. Use this inside lists where an outer
 *   wrapper already owns the parent scope and the route resolver has loaded
 *   everything.
 */
export type ScopedPaletteResolveMode = 'auto' | 'skip-fetch' | 'skip-ancestors' | 'manual';

/**
 * Where the directive applies its `scoped-palette-*` classes.
 *
 * - `'self'` (default): apply to the directive's host element. CSS variables
 *   inherit down to descendants in the host's subtree.
 * - `'body'`: apply to `document.body` (and set the `scopedPalette` marker
 *   attribute so the SCSS defaults match). Use this when content escapes the
 *   host's subtree — CDK overlays/portals, fullscreen modes, app-shell-level
 *   theming. Cleaned up on directive destroy. Intended for a single instance
 *   at a time; concurrent body-mode directives are not coordinated.
 */
export type ScopedPaletteApplyTarget = 'self' | 'body';

@Directive({
  selector: '[scopedPalette]',
  standalone: true,
})
export class ScopedPaletteDirective {
  resolveMode = input<ScopedPaletteResolveMode>('auto');
  applyScopedPaletteOn = input<ScopedPaletteApplyTarget>('self');

  methodId = input<number | undefined>(undefined);
  eduContentBookId = input<number | undefined>(undefined);
  learningDomainId = input<number | undefined>(undefined);
  eduContentTOCId = input<number | undefined>(undefined);

  private element = inject<ElementRef<HTMLElement>>(ElementRef);
  private renderer = inject(Renderer2);
  private styleInjector = inject(StyleInjectorService);
  private store = inject(Store);
  private document = inject(DOCUMENT);

  private currentClasses: string[] = [];
  private currentTarget: HTMLElement | null = null;
  private bodyAttributeSet = false;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.cleanupBodyTarget());
    effect((onCleanup) => this.runPaletteEffect(onCleanup));
  }

  private cleanupBodyTarget(): void {
    if (this.currentTarget !== this.document.body) return;

    this.clearCurrentTarget();

    this.currentClasses = [];
    this.currentTarget = null;
  }

  // Removes the directive's classes from `currentTarget` and clears the
  // `scopedPalette` marker attribute when the target is `<body>`. Used both
  // on destroy and when the active target switches mid-life.
  private clearCurrentTarget(): void {
    if (!this.currentTarget) return;

    for (const c of this.currentClasses) this.renderer.removeClass(this.currentTarget, c);

    if (this.currentTarget === this.document.body && this.bodyAttributeSet) {
      this.renderer.removeAttribute(this.document.body, 'scopedPalette');
      this.bodyAttributeSet = false;
    }
  }

  private runPaletteEffect(onCleanup: EffectCleanupRegisterFn): void {
    const methodIdInput = this.methodId() ?? undefined;
    const bookIdInput = this.eduContentBookId() ?? undefined;
    const learningDomainIdInput = this.learningDomainId() ?? undefined;
    const tocIdInput = this.eduContentTOCId() ?? undefined;

    if (methodIdInput == null && bookIdInput == null && learningDomainIdInput == null && tocIdInput == null) {
      this.applyApplications([]);
      return;
    }

    const sub = this.resolveApplications$(methodIdInput, bookIdInput, learningDomainIdInput, tocIdInput)
      .pipe(distinctUntilChanged(applicationsEqual))
      .subscribe((applications) => {
        if (this.shouldDispatch()) this.dispatchEnsureLoaded(applications);

        this.applyApplications(applications);

        for (const app of applications) {
          this.styleInjector.ensureInjected(buildScopeKey(app.kind, app.id), app.depth);
        }
      });

    onCleanup(() => sub.unsubscribe());
  }

  private shouldDispatch(): boolean {
    const mode = this.resolveMode();
    return mode === 'auto' || mode === 'skip-ancestors';
  }

  private shouldWalkAncestors(): boolean {
    const mode = this.resolveMode();
    return mode === 'auto' || mode === 'skip-fetch';
  }

  // Resolves the full list of scope applications. In ancestor-walking modes,
  // expands `tocIdInput` into the chain of toc ancestors plus the LDs linked
  // to each, then walks toc → book via treeId and book → method via
  // methodId. In non-walking modes, applies only the ids passed in
  // explicitly, all at depth 0.
  private resolveApplications$(
    methodIdInput: number | undefined,
    bookIdInput: number | undefined,
    learningDomainIdInput: number | undefined,
    tocIdInput: number | undefined
  ): Observable<ScopeApplication[]> {
    if (!this.shouldWalkAncestors()) {
      return of(buildExplicitApplications(methodIdInput, bookIdInput, learningDomainIdInput, tocIdInput));
    }

    // Use `getAllEntities` (a single, stable selector reference) and walk the
    // ancestor chain inline rather than `getAllAncestorsForToc(props)` — the
    // factory variant returns a fresh memoized selector per call which is
    // awkward to override in MockStore-based tests.
    const tocEntities$ = this.store.pipe(select(EduContentTocQueries.getAllEntities));

    const ldsByTocId$ = this.store.pipe(
      select(EduContentTocLearningDomainFeature.feature.selectLearningDomainIdsByTocId)
    );

    const tocAndLdApps$ = combineLatest([tocEntities$, ldsByTocId$]).pipe(
      map(([tocEntities, ldsByTocId]) => {
        const chain = tocIdInput != null ? buildAncestorChain(tocEntities ?? {}, tocIdInput) : [];
        return buildTocAndLdApplications(chain, ldsByTocId ?? {}, tocIdInput, learningDomainIdInput);
      })
    );

    return tocAndLdApps$.pipe(
      switchMap((tocAndLdApps) => {
        const deepestTocId = pickDeepestTocId(tocAndLdApps, tocIdInput);
        return this.resolveBookId$(bookIdInput, deepestTocId).pipe(
          switchMap((eduContentBookId) =>
            this.resolveMethodId$(methodIdInput, eduContentBookId).pipe(
              map((methodId) => {
                const apps = [...tocAndLdApps];

                if (eduContentBookId != null) apps.push({ kind: 'eduContentBook', id: eduContentBookId, depth: 0 });
                if (methodId != null) apps.push({ kind: 'method', id: methodId, depth: 0 });

                return apps;
              })
            )
          )
        );
      })
    );
  }

  private resolveBookId$(
    bookIdInput: number | undefined,
    deepestTocId: number | undefined
  ): Observable<number | undefined> {
    if (bookIdInput != null) return of(bookIdInput);
    if (deepestTocId == null) return of(undefined);
    return this.store.pipe(
      select(EduContentTocQueries.getById, { id: deepestTocId }),
      map((toc) => toc?.treeId)
    );
  }

  private resolveMethodId$(
    methodIdInput: number | undefined,
    bookId: number | undefined
  ): Observable<number | undefined> {
    if (methodIdInput != null) return of(methodIdInput);
    if (bookId == null) return of(undefined);

    return this.store.pipe(
      select(EduContentBookQueries.getById, { id: bookId }),
      map((book) => book?.methodId)
    );
  }

  // Dispatches `ensureLoadedFor` for the most specific id only. The backend
  // returns the relevant ancestor palettes alongside the requested level, so a
  // single request covers the whole chain. On routes where a resolver already
  // fetched, the effect is a no-op.
  private dispatchEnsureLoaded(applications: readonly ScopeApplication[]): void {
    let deepestToc: ScopeApplication | undefined;

    for (const a of applications) {
      if (a.kind !== 'eduContentTOC') continue;
      if (!deepestToc || a.depth > deepestToc.depth) deepestToc = a;
    }

    const mostSpecific =
      deepestToc ??
      applications.find((a) => a.kind === 'eduContentBook') ??
      applications.find((a) => a.kind === 'method');
    if (!mostSpecific) return;

    this.store.dispatch(actions.ensureLoadedFor({ level: mostSpecific.kind, id: mostSpecific.id }));
  }

  private applyApplications(applications: readonly ScopeApplication[]): void {
    const newClasses = applications.map((a) => buildScopeKey(a.kind, a.id));
    const target = this.applyScopedPaletteOn() === 'body' ? this.document.body : this.element.nativeElement;

    if (this.currentTarget && this.currentTarget !== target) {
      this.clearCurrentTarget();
      this.currentClasses = [];
    }

    for (const c of this.currentClasses) {
      if (!newClasses.includes(c)) this.renderer.removeClass(target, c);
    }

    for (const c of newClasses) {
      if (!this.currentClasses.includes(c)) this.renderer.addClass(target, c);
    }

    if (target === this.document.body) {
      if (newClasses.length > 0 && !this.bodyAttributeSet) {
        this.renderer.setAttribute(this.document.body, 'scopedPalette', '');
        this.bodyAttributeSet = true;
      } else if (newClasses.length === 0 && this.bodyAttributeSet) {
        this.renderer.removeAttribute(this.document.body, 'scopedPalette');
        this.bodyAttributeSet = false;
      }
    }

    this.currentClasses = newClasses;
    this.currentTarget = newClasses.length > 0 ? target : null;
  }
}
